import type { ReactNode } from 'react';
import { sellerLabel } from '~/utils';
import { unmaskPrimaryContact } from '~/api/sellerContacts';
import { SellerContacts } from './SellerContacts';
import {
  COMPARISON_GRID_ROW_CLASS,
  SELLER_COLUMN_WIDTH_CLASS,
  STICKY_ATTRIBUTE_LABEL_CLASS,
} from './comparisonGridLayout';
import type { Quote } from './splitAwardGridTypes';
import { QuoteSellerName } from './QuoteSellerName';
import { SellerOfferNotice } from './SellerOfferNotice';
import { QuoteReadyToShip } from './QuoteReadyToShip';

export type InlineSellerResponse = Readonly<{
  submit: () => void;
  isSubmitting: boolean;
}>;

export type RenderQuoteActions = (
  quote: Quote,
  onBuyerResponded?: (quoteId: string) => void,
  sellerResponse?: InlineSellerResponse
) => ReactNode;

const NO_SELLER_RESPONSE = () => undefined;

type SellerQuoteActionSlotsProps = Readonly<{
  quotes: ReadonlyArray<Quote>;
  renderQuoteActions: RenderQuoteActions;
  onBuyerResponded?: (quoteId: string) => void;
}>;

export function SellerActionRow({
  quotes,
  renderQuoteActions,
  onBuyerResponded,
  cellPaddingClass,
  sellerResponseOf = NO_SELLER_RESPONSE,
}: SellerQuoteActionSlotsProps &
  Readonly<{
    cellPaddingClass: string;
    sellerResponseOf?: (quote: Quote) => InlineSellerResponse | undefined;
  }>) {
  return (
    <tr role="row" className={COMPARISON_GRID_ROW_CLASS}>
      <th
        role="rowheader"
        className={`${STICKY_ATTRIBUTE_LABEL_CLASS} ${cellPaddingClass}`}
      />
      {quotes.map((quote) => (
        <td
          key={quote.id}
          role="cell"
          className={`${SELLER_COLUMN_WIDTH_CLASS} ${cellPaddingClass}`}
        >
          {renderQuoteActions(quote, onBuyerResponded, sellerResponseOf(quote))}
        </td>
      ))}
    </tr>
  );
}

export function SellerQuoteActionList({
  quotes,
  renderQuoteActions,
  onBuyerResponded,
}: SellerQuoteActionSlotsProps) {
  return (
    <ul className="flex flex-col gap-2">
      {quotes.map((quote) => (
        <li key={quote.id} className="flex items-start justify-between gap-3">
          <div className="text-sm font-semibold text-neut-prim">
            <QuoteSellerName quote={quote} />
            <SellerOfferNotice quote={quote} />
            <SellerContacts
              sellerName={sellerLabel(quote.sellingOrganization)}
              recipients={quote.recipients}
              primaryContactId={unmaskPrimaryContact(quote.primaryContact)?.id}
            />
            <QuoteReadyToShip quote={quote} />
          </div>
          {renderQuoteActions(quote, onBuyerResponded)}
        </li>
      ))}
    </ul>
  );
}
